/**
 * Faixa de resumo da lista: quatro numeros que dizem, antes de qualquer
 * linha, quanto saiu, quanto voltou, quanto ainda esta em jogo e quanto
 * ficou parado -- cada um com a comparacao contra o periodo anterior quando
 * ela faz sentido.
 *
 * "Na regua agora" e RETRATO (o que o motor segura neste instante entre quem
 * entrou na janela), nao serie: por isso nao tem delta. "Sem voltar" e o
 * unico em que subir e ruim.
 *
 * Recurso proprio (`/api/leads/summary`) com loading/erro proprios: a falha
 * dessa rota nova nao pode derrubar a lista, que ja funciona no backend.
 */

import type { AsyncState } from '../../../lib/api';
import { Button, Icon, type IconName } from '../../../components/ui';
import { formatBRLFromCents, formatCount, formatCountCompact, formatRatio } from '../../../lib/format';
import type { ResumoDeLeads } from '../types';
import { delta, LinhaDelta, type SentidoDoDelta } from './Delta';

const ICONE_TOM: Readonly<Record<'accent' | 'good' | 'info' | 'warning', string>> = {
  accent: 'bg-accent/10 text-accent',
  good: 'bg-status-good/10 text-status-good',
  info: 'bg-info/10 text-info',
  warning: 'bg-status-warning/15 text-status-warning',
};

function Tile({
  icon,
  tom,
  label,
  dica,
  value,
  support,
  delta: deltaNode,
}: {
  readonly icon: IconName;
  readonly tom: keyof typeof ICONE_TOM;
  readonly label: string;
  readonly dica: string;
  readonly value: string;
  readonly support: string;
  readonly delta: JSX.Element;
}): JSX.Element {
  return (
    <article className="flex flex-col gap-1.5 rounded-2xl border border-line bg-surface px-5 py-4 shadow-soft">
      <h3 className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wide text-ink-muted" title={dica}>
        <span aria-hidden="true" className={`grid h-7 w-7 place-items-center rounded-lg ${ICONE_TOM[tom]}`}>
          <Icon name={icon} size="sm" />
        </span>
        {label}
        <Icon name="info" size="xs" className="text-ink-subtle" />
      </h3>
      <p className="text-2xl font-semibold tracking-tight text-ink sm:text-3xl">{value}</p>
      <p className="text-xs text-ink-subtle">{support}</p>
      {deltaNode}
    </article>
  );
}

export function ResumoDoPeriodo({ estado, onRetry }: { readonly estado: AsyncState<ResumoDeLeads>; readonly onRetry: () => void }): JSX.Element {
  if (estado.status === 'loading') {
    return (
      <div aria-hidden="true" className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="h-32 animate-pulse rounded-2xl border border-line bg-surface" />
        ))}
      </div>
    );
  }
  if (estado.status === 'error') {
    return (
      <div role="alert" className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-line bg-surface px-5 py-4 shadow-soft">
        <p className="flex items-center gap-2 text-sm text-ink-muted">
          <span aria-hidden="true" className="h-2 w-2 shrink-0 rounded-full bg-status-critical" />
          O resumo do período não carregou ({estado.error.userMessage}). A lista abaixo continua válida.
        </p>
        <Button variant="secondary" size="sm" icon="atualizar" onClick={onRetry}>
          Tentar de novo
        </Button>
      </div>
    );
  }

  const r = estado.data;
  const anterior = r.previous;
  const sentidoNeutro: SentidoDoDelta = 'neutro';

  return (
    <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
      <Tile
        icon="carrinho"
        tom="accent"
        label="Saíram"
        dica="Pessoas que abandonaram o checkout (30 minutos sem pagar) ou tiveram o plano vencido no período. É a base de toda taxa desta tela."
        value={formatCountCompact(r.abandoned)}
        support={`${formatBRLFromCents(r.abandonedAmountCents)} em disputa`}
        delta={<LinhaDelta d={anterior === null ? null : delta(r.abandoned, anterior.abandoned)} sentido={sentidoNeutro} formatar={formatCount} />}
      />
      <Tile
        icon="atualizar"
        tom="good"
        label="Recuperados"
        dica="Pagaram depois de um contato, em até 7 dias. A comissão é calculada sobre o valor pago, com o percentual congelado na venda (ADR-001)."
        value={formatCountCompact(r.recovered)}
        support={`${formatBRLFromCents(r.recoveredAmountCents)} · taxa ${formatRatio(r.recovered, r.abandoned)} · comissão ${formatBRLFromCents(r.commissionCents)}`}
        delta={<LinhaDelta d={anterior === null ? null : delta(r.recovered, anterior.recovered)} sentido="maior-melhor" formatar={formatCount} />}
      />
      <Tile
        icon="raio"
        tom="info"
        label="Na régua agora"
        dica="Retrato deste instante: quem entrou no período e ainda está na fila ou recebendo toques. Fora da comparação com o período anterior."
        value={formatCountCompact(r.inRule)}
        support={`${formatBRLFromCents(r.inRuleAmountCents)} ainda em jogo${r.withSeller > 0 ? ` · ${formatCount(r.withSeller)} com o comercial` : ''}`}
        delta={<p className="text-xs text-ink-subtle">Retrato de agora, sem comparação</p>}
      />
      <Tile
        icon="escudo"
        tom="warning"
        label="Sem voltar"
        dica="Esgotaram a régua ou o motor falhou, sem pagar. Continuam no histórico e podem ser recuperados por contato humano; se a venda sair em até 7 dias do último contato, ainda há comissão."
        value={formatCountCompact(r.exhausted)}
        support={`${formatBRLFromCents(r.exhaustedAmountCents)} parados${r.highValueOpen > 0 ? ` · ${formatCount(r.highValueOpen)} de alto valor em aberto (${formatBRLFromCents(r.highValueOpenAmountCents)})` : ''}`}
        delta={<LinhaDelta d={anterior === null ? null : delta(r.exhausted, anterior.exhausted)} sentido="menor-melhor" formatar={formatCount} />}
      />
    </div>
  );
}
